package com.bulut.mecitogullari.ui.screens.debts

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExtendedFloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.foundation.background
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.util.formatAmountPrecise
import com.bulut.mecitogullari.core.util.formatDateShort
import com.bulut.mecitogullari.data.model.AccountKind
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DebtsScreen(
    onSupplierClick: (supplierId: String) -> Unit,
    onBack: () -> Unit,
    viewModel: DebtsViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    var addDialogKind by remember { mutableStateOf<AccountKind?>(null) }
    val strings = LocalStrings.current

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            TopAppBar(
                title = { Text(strings.debtTracking) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                    }
                },
                actions = {
                    Column(horizontalAlignment = Alignment.End, modifier = Modifier.padding(end = 16.dp)) {
                        if (uiState.totalDebt > 0) {
                            Text(
                                text = "${strings.debtLabel} ${formatAmountPrecise(uiState.totalDebt)}",
                                style = MaterialTheme.typography.labelMedium,
                                color = AppColors.Expense,
                            )
                        }
                        if (uiState.totalReceivable > 0) {
                            Text(
                                text = "${strings.receivableLabel} ${formatAmountPrecise(uiState.totalReceivable)}",
                                style = MaterialTheme.typography.labelMedium,
                                color = AppColors.Income,
                            )
                        }
                    }
                },
            )
        },
        floatingActionButton = {
            Column(horizontalAlignment = Alignment.End) {
                ExtendedFloatingActionButton(
                    onClick = { addDialogKind = AccountKind.CUSTOMER },
                    containerColor = AppColors.Income,
                    contentColor = Color.White,
                    icon = { Icon(Icons.Default.Add, contentDescription = null) },
                    text = { Text(strings.addCustomer) },
                )
                ExtendedFloatingActionButton(
                    onClick = { addDialogKind = AccountKind.SUPPLIER },
                    containerColor = AppColors.Navy,
                    contentColor = Color.White,
                    icon = { Icon(Icons.Default.Add, contentDescription = null) },
                    text = { Text(strings.addSupplier) },
                    modifier = Modifier.padding(top = 12.dp),
                )
            }
        },
    ) { padding ->
        if (uiState.suppliers.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize().padding(padding)) {
                EmptyState(strings.debtsEmpty)
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize().padding(padding),
                contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 96.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                items(uiState.suppliers, key = { it.supplier.id }) { data ->
                    SupplierCard(data = data, onClick = { onSupplierClick(data.supplier.id) })
                }
            }
        }
    }

    addDialogKind?.let { kind ->
        AddAccountDialog(
            kind = kind,
            onDismiss = { addDialogKind = null },
            onCreate = { name, phone, notes ->
                viewModel.createAccount(kind, name, phone, notes)
                addDialogKind = null
            },
        )
    }
}

@Composable
private fun SupplierCard(
    data: SupplierCardData,
    onClick: () -> Unit,
) {
    val strings = LocalStrings.current
    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        val isCustomer = data.supplier.kind == AccountKind.CUSTOMER
        val kindColor = if (isCustomer) AppColors.Income else AppColors.Navy
        Row(
            modifier = Modifier.fillMaxWidth().padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // Initial-letter avatar tinted by account kind.
            Box(
                modifier = Modifier
                    .size(44.dp)
                    .background(kindColor.copy(alpha = 0.14f), RoundedCornerShape(12.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = data.supplier.name.trim().take(1).uppercase(),
                    style = MaterialTheme.typography.titleMedium,
                    color = kindColor,
                    fontWeight = FontWeight.Bold,
                )
            }

            Column(modifier = Modifier.weight(1f).padding(horizontal = 12.dp)) {
                Text(
                    text = data.supplier.name,
                    style = MaterialTheme.typography.titleSmall,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 3.dp)) {
                    Text(
                        text = if (isCustomer) strings.customer else strings.supplier,
                        style = MaterialTheme.typography.labelSmall,
                        color = kindColor,
                        fontWeight = FontWeight.SemiBold,
                    )
                    data.lastEntryEpochMillis?.let {
                        Text(
                            text = "  •  ${formatDateShort(it)}",
                            style = MaterialTheme.typography.labelSmall,
                            color = AppColors.TextSecondary,
                        )
                    }
                }
            }

            Column(horizontalAlignment = Alignment.End) {
                // Positive balance: supplier -> we owe (Borç, red);
                // customer -> they owe us (Alacak, green).
                val positiveIsReceivable = isCustomer
                Text(
                    text = formatAmountPrecise(kotlin.math.abs(data.balance)),
                    style = MaterialTheme.typography.titleSmall,
                    maxLines = 1,
                    color = when {
                        data.balance == 0.0 -> AppColors.TextSecondary
                        (data.balance > 0) == positiveIsReceivable -> AppColors.Income
                        else -> AppColors.Expense
                    },
                )
                Text(
                    text = when {
                        data.balance == 0.0 -> strings.closedLabel
                        (data.balance > 0) == positiveIsReceivable -> strings.receivableLabel
                        else -> strings.debtLabel
                    },
                    style = MaterialTheme.typography.labelSmall,
                    color = AppColors.TextSecondary,
                )
            }
        }
    }
}

@Composable
private fun AddAccountDialog(
    kind: AccountKind,
    onDismiss: () -> Unit,
    onCreate: (name: String, phone: String, notes: String) -> Unit,
) {
    var name by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var notes by remember { mutableStateOf("") }
    val strings = LocalStrings.current

    val isCustomer = kind == AccountKind.CUSTOMER
    val kindColor = if (isCustomer) AppColors.Income else AppColors.Navy

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(
                modifier = Modifier.padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(40.dp)
                            .background(kindColor.copy(alpha = 0.14f), RoundedCornerShape(12.dp)),
                        contentAlignment = Alignment.Center,
                    ) {
                        Icon(
                            Icons.Default.Add,
                            contentDescription = null,
                            tint = kindColor,
                        )
                    }
                    Column(modifier = Modifier.padding(start = 12.dp)) {
                        Text(
                            text = if (isCustomer) strings.newCustomer else strings.newSupplier,
                            style = MaterialTheme.typography.titleMedium,
                        )
                        Text(
                            text = if (isCustomer) strings.customerAccountSubtitle else strings.supplierAccountSubtitle,
                            style = MaterialTheme.typography.bodySmall,
                            color = AppColors.TextSecondary,
                        )
                    }
                }

                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text(if (isCustomer) strings.customerNameLabel else strings.firmNameLabel) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )
                OutlinedTextField(
                    value = phone,
                    onValueChange = { phone = it },
                    label = { Text(strings.phoneOptional) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text(strings.noteOptional) },
                    minLines = 2,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = { onCreate(name, phone, notes) },
                        enabled = name.isNotBlank(),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = kindColor),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.add, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}
